import React from 'react';

import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';

import { useQueryProtocol } from 'client/app/apps/protocols/api/ProtocolsAPI';
import { StepsProvider } from 'client/app/apps/protocols/context/StepsProvider/StepsProvider';
import { WorkflowProvider } from 'client/app/apps/protocols/context/WorkflowProvider';
import { ProtocolProvider } from 'client/app/apps/protocols/EditProtocol/context/ProtocolProvider';
import { useProtocolNavigation } from 'client/app/apps/protocols/EditProtocol/useProtocolNavigation';
import { StepTabViewProvider } from 'client/app/apps/protocols/StepTabProvider';
import UIErrorBox from 'client/app/components/UIErrorBox';
import { ConfiguredDevicesProvider } from 'client/app/state/ConfiguredDevicesProvider/ConfiguredDevicesProvider';
import Button from 'common/ui/components/Button';
import LinearProgress from 'common/ui/components/LinearProgress';
import { AdditionalPanelProvider } from 'common/ui/providers/AdditionalPanelProvider';

type Props = {
  id: ProtocolId;
  version: ProtocolVersion;
};

const EditProtocolScreen: React.FC<Props> = ({ id, version }: Props) => {
  const { data, loading, error } = useQueryProtocol(id, version);
  const { Content, previous, next, publishProtocolDialog } = useProtocolNavigation({
    id,
    version,
    workflowId: data?.protocol.entity.workflow.id,
  });

  if (error) return <UIErrorBox>Could not load protocol</UIErrorBox>;
  if (loading || !data) return <LinearProgress />;

  const { entity, elementContext } = data.protocol;
  const { workflow } = entity;
  const {
    Schema: schema = {},
    Config: { configuredDevices = [] },
  } = workflow.workflow;
  const { steps } = entity.protocol;

  return (
    <StepsProvider steps={steps} schema={schema}>
      <WorkflowProvider workflow={workflow} elementContext={elementContext}>
        <ProtocolProvider entity={entity} version={version}>
          <ConfiguredDevicesProvider devices={configuredDevices}>
            <AdditionalPanelProvider>
              <StepTabViewProvider>
                <Content navigateBack={previous.onClick} />
              </StepTabViewProvider>
            </AdditionalPanelProvider>
          </ConfiguredDevicesProvider>
          {publishProtocolDialog}
          <Footer>
            <Button variant="secondary" onClick={previous.onClick}>
              {previous.label}
            </Button>
            <Button
              variant="primary"
              color="primary"
              onClick={next.onClick}
              disabled={steps.length === 0}
            >
              {next.label}
            </Button>
          </Footer>
        </ProtocolProvider>
      </WorkflowProvider>
    </StepsProvider>
  );
};
export default EditProtocolScreen;

const Footer = styled(Paper)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  position: 'sticky',
  bottom: 0,
  gap: theme.spacing(5),
  padding: theme.spacing(4, 6),
}));
